/* eslint-disable import-x/no-namespace */
import React, { useMemo } from 'react';

import {
  EMOTION_OPTIONS, MASK_OPTIONS, GENDER_OPTIONS, DEEPFAKE_STATE_OPTIONS, AGE_OPTIONS,
} from '@vlabs/api-bindings/src/constants';
import { EVENT_ENUMS, LIVENESS_OPTIONS } from '@vlabs/api-bindings/src/luna-client/constants';

import { useEventApi, useApiData } from '@vlabs/monokit/entities/events/api';
import { createFastFilterDateLabel } from '@vlabs/monokit/shared/lib/fast-filter-date-label';
import { FastFilterGroup } from '@vlabs/monokit/shared/ui/fast-filter-group';

import * as fastFilters from './fast-filters';
import { useEventListFiltersTranslation } from './i18n';
import { hasLocationFilters } from './popovers/location/geolocation';
import { applyMetaFilters, parseMetaFilters } from './popovers/others/meta/serialization';
import { getSimilarityLabel } from './similarity';
import { useEventListFilters } from './useEventListFilters';

const NO_SOURCE_VALUE = 'null';

interface RawOption {
  value: unknown;
  label: string;
}

const labelsOf = (options: RawOption[], values: unknown): string[] => (
  (Array.isArray(values) ? values : []).map((value) => (
    options.find((option) => option.value === value)?.label ?? String(value)
  ))
);

const scalarLabel = (options: RawOption[], value: unknown): string => (
  options.find((option) => option.value === value)?.label ?? String(value)
);

const ageLabel = (gte: number | undefined, lt: number | undefined): string => {
  const option = (AGE_OPTIONS.raw as RawOption[]).find((item) => (
    Array.isArray(item.value) && item.value[0] === gte && item.value[1] === lt
  ));

  return option?.label ?? `${gte ?? ''}–${lt ?? ''}`;
};

interface EventListFastFiltersProps {
  includedAttributes?: string[];
}

function EventListFastFilters({
  includedAttributes = [],
}: EventListFastFiltersProps): React.JSX.Element {
  const { filters, updateFilters, setFilters } = useEventListFilters();
  const { t } = useEventListFiltersTranslation();
  const api = useEventApi();
  const { externalIdField } = api.capabilities;
  const hasTopSimilarCardIds = api.capabilities.topSimilarCardIds;

  const { data: streamOptionsData } = useApiData(async () => api.getStreamOptions());
  const { data: handlerOptionsData } = useApiData(async () => api.getHandlerOptions());
  const { data: listOptionsData } = useApiData(async () => api.getListOptions());

  const sourceOptions = useMemo(() => streamOptionsData ?? [], [streamOptionsData]) as RawOption[];
  const handlerOptions = useMemo(() => handlerOptionsData ?? [], [handlerOptionsData]) as RawOption[];
  const listOptions = useMemo(() => listOptionsData ?? [], [listOptionsData]) as RawOption[];

  const metaRows = parseMetaFilters(filters);
  const similarityLabel = getSimilarityLabel(
    filters.top_similar_object_similarity__gte,
    filters.top_similar_object_similarity__lt,
  );

  const secondaryEventIds = filters[externalIdField];

  return (
    <FastFilterGroup>
      {!!(filters.create_time__gte ?? filters.create_time__lt) && (
        <fastFilters.DateFilterWithBottomPopover
          data-testid="create_time"
          key="create_time"
          onClear={() => updateFilters({ create_time__gte: undefined, create_time__lt: undefined })}
          value={createFastFilterDateLabel({
            createTimeGte: filters.create_time__gte,
            createTimeLt: filters.create_time__lt,
          })}
        />
      )}

      {!!filters.sources && (
        <fastFilters.SourceFilterWithBottomPopover
          data-testid="sources"
          key="sources"
          onClear={() => updateFilters({ sources: undefined })}
          value={(filters.sources ?? []).map((value) => (
            value === NO_SOURCE_VALUE ? t('no_source') : (scalarLabel(sourceOptions, value))
          ))}
        />
      )}

      {!!filters.handler_ids && (
        <fastFilters.HandlersFilterWithBottomPopover
          data-testid="handler_ids"
          key="handler_ids"
          onClear={() => updateFilters({ handler_ids: undefined })}
          value={labelsOf(handlerOptions, filters.handler_ids)}
        />
      )}

      {!!filters.top_matching_candidates_label && (
        <fastFilters.ComparingAttributeFilterWithBottomPopover
          data-testid="top_matching_candidates_label"
          key="top_matching_candidates_label"
          onClear={() => updateFilters({ top_matching_candidates_label: undefined })}
          value={scalarLabel(listOptions, filters.top_matching_candidates_label)}
        />
      )}

      {!!filters.tags?.length && (
        <fastFilters.PermissionTagsFilterWithBottomPopover
          data-testid="tags"
          key="tags"
          onClear={() => updateFilters({ tags: undefined })}
          value={filters.tags}
        />
      )}

      {!!(filters.event_ids?.length ?? secondaryEventIds?.length) && (
        <fastFilters.EventIdFilterWithBottomPopover
          data-testid="event_ids"
          key="event_ids"
          onClear={() => updateFilters({
            event_ids: undefined,
            [externalIdField]: undefined,
          })}
          value={[...(filters.event_ids ?? []), ...(secondaryEventIds ?? [])]}
        />
      )}

      {!!hasTopSimilarCardIds && !!filters.top_similar_card_ids?.length && (
        <fastFilters.TopSimilarCardIdsFilterWithBottomPopover
          data-testid="top_similar_card_ids"
          key="top_similar_card_ids"
          onClear={() => updateFilters({ top_similar_card_ids: undefined })}
          value={filters.top_similar_card_ids}
        />
      )}

      {
        (
          filters.top_similar_object_similarity__gte !== undefined
          || filters.top_similar_object_similarity__lt !== undefined) && (
          <fastFilters.SimilarityFilterWithBottomPopover
            data-testid="top_similar_object_similarity"
            key="top_similar_object_similarity"
            onClear={() => updateFilters({
              top_similar_object_similarity__gte: undefined,
              top_similar_object_similarity__lt: undefined,
            })}
            value={similarityLabel}
          />
        )
      }

      {!!filters.top_similar_object_ids?.length && (
        <fastFilters.TopSimilarObjectsFilterWithBottomPopover
          data-testid="top_similar_object_ids"
          key="top_similar_object_ids"
          onClear={() => updateFilters({ top_similar_object_ids: undefined })}
          value={filters.top_similar_object_ids}
        />
      )}

      {filters.gender !== undefined && (
        <fastFilters.GenderFilterWithBottomPopover
          data-testid="gender"
          key="gender"
          onClear={() => updateFilters({ gender: undefined })}
          value={scalarLabel(GENDER_OPTIONS.raw, filters.gender)}
        />
      )}

      {(filters.age__gte !== undefined || filters.age__lt !== undefined) && (
        <fastFilters.AgeFilterWithBottomPopover
          data-testid="age"
          key="age"
          onClear={() => updateFilters({ age__gte: undefined, age__lt: undefined })}
          value={ageLabel(filters.age__gte, filters.age__lt)}
        />
      )}

      {!!filters.emotions?.length && (
        <fastFilters.EmotionsFilterWithBottomPopover
          data-testid="emotions"
          key="emotions"
          onClear={() => updateFilters({ emotions: undefined })}
          value={labelsOf(EMOTION_OPTIONS.raw, filters.emotions)}
        />
      )}

      {!!filters.masks?.length && (
        <fastFilters.MaskFilterWithBottomPopover
          data-testid="masks"
          key="masks"
          onClear={() => updateFilters({ masks: undefined })}
          value={labelsOf(MASK_OPTIONS.raw, filters.masks)}
        />
      )}

      {!!filters.liveness?.length && (
        <fastFilters.LivenessFilterWithBottomPopover
          data-testid="liveness"
          key="liveness"
          onClear={() => updateFilters({ liveness: undefined })}
          value={labelsOf(LIVENESS_OPTIONS.raw, filters.liveness)}
        />
      )}

      {!!filters.deepfake?.length && (
        <fastFilters.DeepfakeFilterWithBottomPopover
          data-testid="deepfake"
          key="deepfake"
          onClear={() => updateFilters({ deepfake: undefined })}
          value={labelsOf(DEEPFAKE_STATE_OPTIONS.raw, filters.deepfake)}
        />
      )}

      {!!filters.face_ids?.length && (
        <fastFilters.FaceIdFilterWithBottomPopover
          data-testid="face_ids"
          key="face_ids"
          onClear={() => updateFilters({ face_ids: undefined })}
          value={filters.face_ids}
        />
      )}

      {!!filters.apparent_gender?.length && (
        <fastFilters.ApparentGenderFilterWithBottomPopover
          data-testid="apparent_gender"
          key="apparent_gender"
          onClear={() => updateFilters({ apparent_gender: undefined })}
          value={labelsOf(EVENT_ENUMS.BODY_BASIC_ATTRIBUTES.APPARENT_GENDER.raw, filters.apparent_gender)}
        />
      )}

      {(filters.apparent_age__gte !== undefined || filters.apparent_age__lt !== undefined) && (
        <fastFilters.ApparentAgeFilterWithBottomPopover
          data-testid="apparent_age"
          key="apparent_age"
          onClear={() => updateFilters({ apparent_age__gte: undefined, apparent_age__lt: undefined })}
          value={ageLabel(filters.apparent_age__gte, filters.apparent_age__lt)}
        />
      )}

      {!!(filters.upper_clothing_colors?.length ?? filters.sleeve_lengths?.length) && (
        <fastFilters.UpperAttributesFilterWithBottomPopover
          data-testid="upper_attributes"
          key="upper_attributes"
          onClear={() => updateFilters({ upper_clothing_colors: undefined, sleeve_lengths: undefined })}
          value={[
            ...labelsOf(EVENT_ENUMS.UPPER_BODY.UPPER_CLOSING_COLOR.raw, filters.upper_clothing_colors),
            ...labelsOf(EVENT_ENUMS.UPPER_BODY.SLEEVE.raw, filters.sleeve_lengths),
          ]}
        />
      )}

      {!!(filters.lower_garment_colors?.length ?? filters.lower_garment_types?.length) && (
        <fastFilters.LowerAttributesFilterWithBottomPopover
          data-testid="lower_attributes"
          key="lower_attributes"
          onClear={() => updateFilters({ lower_garment_colors: undefined, lower_garment_types: undefined })}
          value={[
            ...labelsOf(EVENT_ENUMS.LOWER_BODY.LOWER_GARMENT.COLORS.raw, filters.lower_garment_colors),
            ...labelsOf(EVENT_ENUMS.LOWER_BODY.LOWER_GARMENT.TYPE.raw, filters.lower_garment_types),
          ]}
        />
      )}

      {!!filters.shoes_apparent_colors?.length && (
        <fastFilters.ShoesColorFilterWithBottomPopover
          data-testid="shoes_apparent_colors"
          key="shoes_apparent_colors"
          onClear={() => updateFilters({ shoes_apparent_colors: undefined })}
          value={labelsOf(EVENT_ENUMS.LOWER_BODY.SHOES.APPARENT_COLOR.raw, filters.shoes_apparent_colors)}
        />
      )}

      {!!(filters.headwear_apparent_colors?.length ?? filters.headwear_states?.length) && (
        <fastFilters.HeadwearFilterWithBottomPopover
          data-testid="headwear"
          key="headwear"
          onClear={() => updateFilters({ headwear_apparent_colors: undefined, headwear_states: undefined })}
          value={[
            ...labelsOf(EVENT_ENUMS.UPPER_BODY.HEADWEAR_COLORS.raw, filters.headwear_apparent_colors),
            ...labelsOf(EVENT_ENUMS.UPPER_BODY.HEADWEAR.raw, filters.headwear_states),
          ]}
        />
      )}

      {!!filters.backpack_states?.length && (
        <fastFilters.BackpackFilterWithBottomPopover
          data-testid="backpack_states"
          key="backpack_states"
          onClear={() => updateFilters({ backpack_states: undefined })}
          value={labelsOf(EVENT_ENUMS.ACCESSORIES.BACKPACK.raw, filters.backpack_states)}
        />
      )}

      {hasLocationFilters(filters) && (
        <fastFilters.LocationFilterWithBottomPopover
          data-testid="location"
          key="location"
          onClear={() => updateFilters({
            cities: undefined,
            areas: undefined,
            districts: undefined,
            streets: undefined,
            house_numbers: undefined,
            origin_longitude: undefined,
            longitude_delta: undefined,
            origin_latitude: undefined,
            latitude_delta: undefined,
          })}
          popoverProps={{ includedAttributes }}
          value={[
            ...(filters.cities ?? []),
            ...(filters.areas ?? []),
            ...(filters.districts ?? []),
            ...(filters.streets ?? []),
            ...(filters.house_numbers ?? []),
            ...(filters.origin_longitude !== undefined ? [String(filters.origin_longitude)] : []),
            ...(filters.longitude_delta !== undefined ? [`±${filters.longitude_delta}`] : []),
            ...(filters.origin_latitude !== undefined ? [String(filters.origin_latitude)] : []),
            ...(filters.latitude_delta !== undefined ? [`±${filters.latitude_delta}`] : []),
          ]}
        />
      )}

      {!!filters.track_ids?.length && (
        <fastFilters.TrackIdFilterWithBottomPopover
          data-testid="track_ids"
          key="track_ids"
          onClear={() => updateFilters({ track_ids: undefined })}
          value={filters.track_ids}
        />
      )}

      {metaRows.length > 0 && (
        <fastFilters.MetaFilterWithBottomPopover
          data-testid="meta"
          key="meta"
          onClear={() => setFilters(
            applyMetaFilters(filters, []),
          )}
          value={metaRows.map((row) => `${row.key}: ${row.value}`)}
        />
      )}
    </FastFilterGroup>
  );
}

export { EventListFastFilters };
